import { updateMediaElementAction } from 'editor/src/store/design/slice';
import { TextValueFormat } from 'editor/src/store/design/types';
import { isSameValueFormat } from 'editor/src/store/design/util/applyValueFormat';
import buildSharedTextValueUpdate from 'editor/src/store/design/util/buildSharedTextValueUpdate';
import {
  getSharedTextFieldMembers,
  isInReflowRow,
  receivesSharedValue,
} from 'editor/src/store/design/util/sharedTextFields';
import isSampleFlagChangeAllowed from 'editor/src/store/editor/selector/isSampleFlagChangeAllowed';
import type { Thunk } from 'editor/src/store/hooks';

import calibrateReflowTextWidthOperation from './calibrateReflowTextWidthOperation';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

export interface SharedTextFieldPatch {
  /**
   * The new string — omitted when only the format moved. A format-only change
   * must leave every member's own text alone: on a group that has drifted
   * apart, pushing the source's string out on the back of a mask edit is the
   * same silent overwrite the Required toggle is kept away from.
   */
  text?: string;
  /** Always applied: the group holds one format, the source's. */
  valueFormat: TextValueFormat | undefined;
}

/**
 * Carries the shared value, and the format it is poured through, onto every
 * other member of the group.
 *
 * This is what makes "entered once" true: the buyer types Name on the front of
 * the card and the back's Name follows, so a two-sided card is not two
 * transcriptions of the same detail that can drift apart.
 *
 * The format goes with the value because the value is stored formatted. A
 * group whose members disagreed about the format would store whichever the
 * buyer's page happened to apply, and print that on the side that asked for
 * something else.
 *
 * Dispatches the plain update action rather than updateMediaElementOperation,
 * for two reasons: the members have to land inside the caller's batch and its
 * single undo entry, and routing them back through the operation would have
 * each mirror mirror again.
 */
const syncSharedTextValueOperation =
  (sourceUuid: number, sharedFieldId: string, patch: SharedTextFieldPatch): Thunk =>
  (dispatch, getState) => {
    const state = getState();
    const { designData } = state.design;
    const sampleFlagChangeAllowed = isSampleFlagChangeAllowed(state);
    const spreadsToReflow = new Set<number>();
    const { valueFormat } = patch;

    getSharedTextFieldMembers(designData, sharedFieldId).forEach(({ element, address }) => {
      const text = patch.text ?? element.extra.text;
      // Which members the value reaches is receivesSharedValue's to say, and
      // this is the write it exists to hold back: a member it excludes gets
      // the buyer's content pushed into a layer that is not theirs to fill.
      // The author side refuses to build any of these pairings, so the exclusions
      // are reachable here only from a layer changed after it joined, or from
      // imported data — which is exactly why the predicate is asked rather
      // than assumed.
      if (
        element.uuid === sourceUuid ||
        (element.extra.text === text && isSameValueFormat(element.extra.valueFormat, valueFormat)) ||
        !receivesSharedValue(element)
      ) {
        return;
      }

      const elementUpdate = buildSharedTextValueUpdate(element, text, sampleFlagChangeAllowed, valueFormat);
      dispatch(calibrateReflowTextWidthOperation(address, element, elementUpdate));
      dispatch(
        updateMediaElementAction({
          elementAddress: address,
          elementUpdate,
        }),
      );

      // Only a string that actually moved can change a member's measured width
      // and flip its row's packing. A format-only write leaves the text as it
      // was, so re-packing on it would be work with nothing to find.
      if (element.extra.text !== text && isInReflowRow(designData, address.spreadIndex, element.uuid)) {
        spreadsToReflow.add(address.spreadIndex);
      }
    });

    // Once per spread, after every member on it has been written: a row packs
    // from the widths of all its members, so re-packing between two of them
    // would measure a half-updated row.
    spreadsToReflow.forEach((spreadIndex) => {
      dispatch(updateHiddenElementsOperation(spreadIndex));
    });
  };

export default syncSharedTextValueOperation;
